
import React, { useState, useRef } from 'react';
import { useModal } from '../../context/ModalContext';
import {
  Church,
  Wifi,
  Smartphone,
  Users,
  Cake,
  Megaphone,
  BarChart3,
  Save,
  Lock,
  Plug,
  CheckCircle2,
  AlertTriangle,
  Send,
  Plus,
  X,
  Rocket,
  RefreshCw,
  Check,
  PartyPopper,
  MessageCircle,
  BookOpen,
  SkipForward,
} from 'lucide-react';

const Onboarding = ({ showToast, onNavigate }) => {
  const [steps, setSteps] = useState([
    { 
      id: 1, 
      n: 1, 
      title: 'Church Profile', 
      desc: 'Set up your church name, pastor, and contact details', 
      done: false,
      icon: Church,
      path: 'settings',
      component: 'ChurchProfile'
    },
    { 
      id: 2, 
      n: 2, 
      title: 'SMS Provider', 
      desc: 'Connect Africa\'s Talking, Arkesel, or another provider', 
      done: false,
      icon: Wifi,
      path: 'settings',
      component: 'SmsProvider'
    },
    { 
      id: 3, 
      n: 3, 
      title: 'Shortcode & Keywords', 
      desc: 'Configure your shortcode and registration keywords', 
      done: false,
      icon: Smartphone,
      path: 'shortcode',
      component: 'ShortcodeSetup'
    },
    { 
      id: 4, 
      n: 4, 
      title: 'Import Members', 
      desc: 'Upload your existing member list via CSV', 
      done: false,
      icon: Users,
      current: true,
      path: 'import',
      component: 'BulkImport'
    },
    { 
      id: 5, 
      n: 5, 
      title: 'Birthday Automation', 
      desc: 'Turn on birthday wishes to go live immediately', 
      done: false,
      icon: Cake,
      path: 'birthdays',
      component: 'BirthdayAutomation'
    },
    { 
      id: 6, 
      n: 6, 
      title: 'First Broadcast', 
      desc: 'Send your first SMS broadcast to all members', 
      done: false,
      icon: Megaphone,
      path: 'broadcast',
      component: 'Broadcast'
    },
    { 
      id: 7, 
      n: 7, 
      title: 'Set Up Polls', 
      desc: 'Create your first service feedback poll', 
      done: false,
      icon: BarChart3,
      path: 'polls',
      component: 'PollSetup'
    }
  ]);

  // State for Church Profile
  const [churchProfile, setChurchProfile] = useState({
    name: '',
    pastor: '',
    phone: '',
    email: '',
    address: '',
    website: ''
  });

  // State for SMS Provider
  const [smsProvider, setSmsProvider] = useState({
    provider: '',
    apiKey: '',
    shortcode: '',
    senderId: ''
  });

  // State for Shortcode Data
  const [shortcodeData, setShortcodeData] = useState({
    shortcode: '',
    keyword: '',
    flowSteps: []
  });

  // State for Birthday Data
  const [birthdayData, setBirthdayData] = useState({
    enabled: false,
    message: 'Happy Birthday {FirstName}! 🎂 May your day be filled with God\'s love and blessings! 🙏',
    sendTime: '08:00',
    timezone: 'GMT'
  });

  // State for Broadcast Data
  const [broadcastData, setBroadcastData] = useState({
    message: '',
    recipients: 'All Members',
    scheduleDate: ''
  });

  // Poll state - these will be used to store the final values
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollExpiresIn, setPollExpiresIn] = useState('7 days');

  // Refs for poll options - like Members component uses refs for editing
  const pollQuestionRef = useRef(null);
  const pollOptionRefs = useRef([]);
  const pollExpiresInRef = useRef(null);

  const { openModal, closeModal } = useModal();

  const completedCount = steps.filter(s => s.done).length;
  const pct = Math.round(completedCount / steps.length * 100);

  const markStepDone = (stepId) => {
    setSteps(steps.map(s => 
      s.id === stepId ? { ...s, done: true, current: false } : s
    ));
  };

  const handleStartStep = (step) => {
    if (step.done) {
      if (step.path && onNavigate) {
        onNavigate(step.path);
        showToast(`📋 Opening ${step.title}...`, 'info');
      }
      return;
    }

    setSteps(steps.map(s => ({
      ...s,
      current: s.id === step.id
    })));

    if (step.id === 4 && step.path) {
      window.location.href = `/${step.path}`;
      showToast(`📋 Opening ${step.title}...`, 'info');
      return;
    }

    openStepModal(step);
  };

  const openStepModal = (step) => {
    switch(step.component) {
      case 'ChurchProfile':
        openChurchProfileModal(step);
        break;
      case 'SmsProvider':
        openSmsProviderModal(step);
        break;
      case 'ShortcodeSetup':
        openShortcodeModal(step);
        break;
      case 'BulkImport':
        if (step.path) {
          window.location.href = `/${step.path}`;
          showToast(`📋 Opening ${step.title}...`, 'info');
        }
        break;
      case 'BirthdayAutomation':
        openBirthdayModal(step);
        break;
      case 'Broadcast':
        openBroadcastModal(step);
        break;
      case 'PollSetup':
        openPollModal(step);
        break;
      default:
        openGenericModal(step);
    }
  };

  // 1. Church Profile Modal
  const openChurchProfileModal = (step) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Church size={16} style={{ verticalAlign: '-3px' }} /> Step {step.n}: {step.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            {step.desc}
          </p>
          <div className="form-group">
            <label className="form-label">Church Name *</label>
            <input 
              className="form-input" 
              value={churchProfile.name}
              onChange={(e) => setChurchProfile({...churchProfile, name: e.target.value})}
              placeholder="e.g. Grace Chapel International"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Pastor Name *</label>
            <input 
              className="form-input" 
              value={churchProfile.pastor}
              onChange={(e) => setChurchProfile({...churchProfile, pastor: e.target.value})}
              placeholder="e.g. Rev. Dr. John Smith"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number *</label>
            <input 
              className="form-input" 
              value={churchProfile.phone}
              onChange={(e) => setChurchProfile({...churchProfile, phone: e.target.value})}
              placeholder="e.g. +233244123456"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input 
              className="form-input" 
              type="email"
              value={churchProfile.email}
              onChange={(e) => setChurchProfile({...churchProfile, email: e.target.value})}
              placeholder="e.g. info@gracechapel.org"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input 
              className="form-input" 
              value={churchProfile.address}
              onChange={(e) => setChurchProfile({...churchProfile, address: e.target.value})}
              placeholder="e.g. 123 Main Street, Accra"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Website</label>
            <input 
              className="form-input" 
              value={churchProfile.website}
              onChange={(e) => setChurchProfile({...churchProfile, website: e.target.value})}
              placeholder="e.g. https://gracechapel.org"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              if (!churchProfile.name.trim()) {
                showToast('Please enter a church name', 'error');
                return;
              }
              if (!churchProfile.pastor.trim()) {
                showToast('Please enter a pastor name', 'error');
                return;
              }
              if (!churchProfile.phone.trim()) {
                showToast('Please enter a phone number', 'error');
                return;
              }
              markStepDone(step.id);
              showToast(`✅ ${step.title} completed!`, 'success');
              if (step.path && onNavigate) {
                onNavigate(step.path);
              }
              closeModal();
            }}
          >
<Save size={16} style={{ verticalAlign: '-3px' }} /> Save & Complete
          </button>
        </div>
      </div>
    );
  };

  // 2. SMS Provider Modal
  const openSmsProviderModal = (step) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Wifi size={16} style={{ verticalAlign: '-3px' }} /> Step {step.n}: {step.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            {step.desc}
          </p>
          <div className="form-group">
            <label className="form-label">SMS Provider *</label>
            <select 
              className="form-select"
              value={smsProvider.provider}
              onChange={(e) => setSmsProvider({...smsProvider, provider: e.target.value})}
            >
              <option value="">Select Provider</option>
              <option value="africas_talking">Africa's Talking</option>
              <option value="arkesel">Arkesel</option>
              <option value="twilio">Twilio</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">API Key *</label>
            <input 
              className="form-input" 
              type="password"
              value={smsProvider.apiKey}
              onChange={(e) => setSmsProvider({...smsProvider, apiKey: e.target.value})}
              placeholder="Enter your API key"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Shortcode</label>
            <input 
              className="form-input" 
              value={smsProvider.shortcode}
              onChange={(e) => setSmsProvider({...smsProvider, shortcode: e.target.value})}
              placeholder="e.g. 40404"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Sender ID</label>
            <input 
              className="form-input" 
              value={smsProvider.senderId}
              onChange={(e) => setSmsProvider({...smsProvider, senderId: e.target.value})}
              placeholder="e.g. GRACECHAPEL"
            />
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--blue-bg)', 
            borderRadius: '8px', 
            fontSize: '12px', 
            color: 'var(--blue)' 
          }}>
<Lock size={14} style={{ verticalAlign: '-2px' }} /> Your API key is encrypted and stored securely.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              if (!smsProvider.provider) {
                showToast('Please select a provider', 'error');
                return;
              }
              if (!smsProvider.apiKey.trim()) {
                showToast('Please enter an API key', 'error');
                return;
              }
              markStepDone(step.id);
              showToast(`✅ ${step.title} completed!`, 'success');
              if (step.path && onNavigate) {
                onNavigate(step.path);
              }
              closeModal();
            }}
          >
<Plug size={16} style={{ verticalAlign: '-3px' }} /> Connect & Complete
          </button>
        </div>
      </div>
    );
  };

  // 3. Shortcode Setup Modal
  const openShortcodeModal = (step) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Smartphone size={16} style={{ verticalAlign: '-3px' }} /> Step {step.n}: {step.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            {step.desc}
          </p>
          <div className="form-group">
            <label className="form-label">Shortcode Number</label>
            <input 
              className="form-input" 
              value={shortcodeData.shortcode}
              onChange={(e) => setShortcodeData({...shortcodeData, shortcode: e.target.value})}
              placeholder="e.g. 40404"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Registration Keyword</label>
            <input 
              className="form-input" 
              value={shortcodeData.keyword}
              onChange={(e) => setShortcodeData({...shortcodeData, keyword: e.target.value.toUpperCase()})}
              placeholder="e.g. JOIN"
            />
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)'
          }}>
            <div style={{ fontWeight: '600', color: 'var(--navy)', marginBottom: '4px' }}>
              How members will register:
            </div>
            <div style={{ lineHeight: '1.8' }}>
              1. Text <strong>{shortcodeData.keyword || 'JOIN'}</strong> to <strong>{shortcodeData.shortcode || '40404'}</strong><br />
              2. System asks for Full Name<br />
              3. System asks for Date of Birth<br />
              4. System asks for Department<br />
              5. Member added & welcome SMS sent <CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              if (!shortcodeData.shortcode.trim()) {
                showToast('Please enter a shortcode number', 'error');
                return;
              }
              if (!shortcodeData.keyword.trim()) {
                showToast('Please enter a registration keyword', 'error');
                return;
              }
              markStepDone(step.id);
              showToast(`✅ ${step.title} completed!`, 'success');
              if (step.path && onNavigate) {
                onNavigate(step.path);
              }
              closeModal();
            }}
          >
<Save size={16} style={{ verticalAlign: '-3px' }} /> Save & Complete
          </button>
        </div>
      </div>
    );
  };

  // 5. Birthday Automation Modal
  const openBirthdayModal = (step) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Cake size={16} style={{ verticalAlign: '-3px' }} /> Step {step.n}: {step.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            {step.desc}
          </p>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input 
                type="checkbox" 
                checked={birthdayData.enabled}
                onChange={(e) => setBirthdayData({...birthdayData, enabled: e.target.checked})}
              />
              Enable Birthday Automation
            </label>
          </div>
          <div className="form-group">
            <label className="form-label">Birthday Message</label>
            <textarea 
              className="form-textarea" 
              rows="3"
              value={birthdayData.message}
              onChange={(e) => setBirthdayData({...birthdayData, message: e.target.value})}
            />
            <div className="form-hint">Use {'{FirstName}'} for personalisation</div>
          </div>
          <div className="form-group">
            <label className="form-label">Send Time</label>
            <input 
              className="form-input" 
              type="time"
              value={birthdayData.sendTime}
              onChange={(e) => setBirthdayData({...birthdayData, sendTime: e.target.value})}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Time Zone</label>
            <select 
              className="form-select"
              value={birthdayData.timezone}
              onChange={(e) => setBirthdayData({...birthdayData, timezone: e.target.value})}
            >
              <option value="GMT">GMT (UTC+0)</option>
              <option value="GMT+1">GMT+1 (WAT)</option>
              <option value="GMT+2">GMT+2 (CAT)</option>
              <option value="GMT+3">GMT+3 (EAT)</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              markStepDone(step.id);
              showToast(`✅ ${step.title} completed!`, 'success');
              if (step.path && onNavigate) {
                onNavigate(step.path);
              }
              closeModal();
            }}
          >
<Save size={16} style={{ verticalAlign: '-3px' }} /> Save & Complete
          </button>
        </div>
      </div>
    );
  };

  // 6. First Broadcast Modal
  const openBroadcastModal = (step) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Megaphone size={16} style={{ verticalAlign: '-3px' }} /> Step {step.n}: {step.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            {step.desc}
          </p>
          <div className="form-group">
            <label className="form-label">Message</label>
            <textarea 
              className="form-textarea" 
              rows="4"
              value={broadcastData.message}
              onChange={(e) => setBroadcastData({...broadcastData, message: e.target.value})}
              placeholder="Welcome to Grace Chapel! 🙏 We're excited to have you with us..."
            />
            <div className="form-hint">Use {'{FirstName}'}, {'{ChurchName}'} for personalisation</div>
          </div>
          <div className="form-group">
            <label className="form-label">Send To</label>
            <select 
              className="form-select"
              value={broadcastData.recipients}
              onChange={(e) => setBroadcastData({...broadcastData, recipients: e.target.value})}
            >
              <option>All Members</option>
              <option>Active Members</option>
              <option>New Members</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Schedule (Optional)</label>
            <input 
              className="form-input" 
              type="datetime-local"
              value={broadcastData.scheduleDate}
              onChange={(e) => setBroadcastData({...broadcastData, scheduleDate: e.target.value})}
            />
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--amber-bg)', 
            borderRadius: '8px', 
            fontSize: '12px', 
            color: 'var(--amber)' 
          }}>
<AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will send SMS to all members and use credits from your balance.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              if (!broadcastData.message.trim()) {
                showToast('Please enter a message', 'error');
                return;
              }
              markStepDone(step.id);
              showToast(`✅ ${step.title} completed!`, 'success');
              if (step.path && onNavigate) {
                onNavigate(step.path);
              }
              closeModal();
            }}
          >
<Send size={16} style={{ verticalAlign: '-3px' }} /> Send & Complete
          </button>
        </div>
      </div>
    );
  };

  // 7. Set Up Polls Modal - Using refs like Members component
  const openPollModal = (step) => {
    // Set initial values in refs after modal opens
    setTimeout(() => {
      if (pollQuestionRef.current) {
        pollQuestionRef.current.value = pollQuestion;
      }
      // Set each option ref
      pollOptionRefs.current.forEach((ref, index) => {
        if (ref && pollOptions[index] !== undefined) {
          ref.value = pollOptions[index];
        }
      });
      if (pollExpiresInRef.current) {
        pollExpiresInRef.current.value = pollExpiresIn;
      }
    }, 50);

    // Helper to get current poll values from refs
    const getPollValues = () => {
      const question = pollQuestionRef.current?.value || '';
      const options = pollOptionRefs.current.map(ref => ref?.value || '');
      const expiresIn = pollExpiresInRef.current?.value || '7 days';
      return { question, options, expiresIn };
    };

    // Add option handler - updates refs and state
    const handleAddOption = () => {
      if (pollOptions.length < 5) {
        const newOptions = [...pollOptions, ''];
        setPollOptions(newOptions);
        // Update refs after state change
        setTimeout(() => {
          pollOptionRefs.current = pollOptionRefs.current.slice(0, newOptions.length);
          // Set the new ref value
          if (pollOptionRefs.current[newOptions.length - 1]) {
            pollOptionRefs.current[newOptions.length - 1].value = '';
          }
        }, 10);
        showToast('➕ Option added', 'success');
      } else {
        showToast('⚠️ Maximum 5 options allowed', 'info');
      }
    };

    // Remove option handler
    const handleRemoveOption = (index) => {
      if (pollOptions.length > 2) {
        const newOptions = pollOptions.filter((_, i) => i !== index);
        setPollOptions(newOptions);
        // Update refs after state change
        setTimeout(() => {
          pollOptionRefs.current = pollOptionRefs.current.filter((_, i) => i !== index);
        }, 10);
        showToast('🗑️ Option removed', 'info');
      } else {
        showToast('⚠️ Minimum 2 options required', 'info');
      }
    };

    // Remove last option handler
    const handleRemoveLastOption = () => {
      if (pollOptions.length > 2) {
        const newOptions = pollOptions.slice(0, -1);
        setPollOptions(newOptions);
        // Update refs after state change
        setTimeout(() => {
          pollOptionRefs.current = pollOptionRefs.current.slice(0, -1);
        }, 10);
        showToast('🗑️ Last option removed', 'info');
      } else {
        showToast('⚠️ Minimum 2 options required', 'info');
      }
    };

    // Save poll values from refs to state
    const savePollValues = () => {
      const values = getPollValues();
      setPollQuestion(values.question);
      setPollOptions(values.options);
      setPollExpiresIn(values.expiresIn);
    };

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Step {step.n}: {step.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            {step.desc}
          </p>
          
          <div className="form-group">
            <label className="form-label">Poll Question *</label>
            <input 
              className="form-input" 
              ref={pollQuestionRef}
              defaultValue=""
              placeholder="e.g. How was today's service?"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Options * <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '400' }}>(minimum 2)</span></label>
            
            <div className="poll-options-container">
              {pollOptions.map((option, index) => (
                <div key={`option-${index}`} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  <div style={{ 
                    minWidth: '24px', 
                    height: '24px', 
                    borderRadius: '50%', 
                    background: 'var(--navy)', 
                    color: 'var(--gold-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: '700',
                    flexShrink: 0
                  }}>
                    {String.fromCharCode(65 + index)}
                  </div>
                  <input 
                    className="form-input" 
                    ref={el => pollOptionRefs.current[index] = el}
                    defaultValue={option}
                    placeholder={`Option ${index + 1}`}
                    style={{ flex: 1 }}
                  />
                  {pollOptions.length > 2 && (
                    <button 
                      className="btn btn-ghost btn-sm btn-icon" 
                      onClick={() => handleRemoveOption(index)}
                      style={{ color: 'var(--red)' }}
                      title="Remove option"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
              {pollOptions.length < 5 && (
                <button 
                  className="btn btn-gold btn-sm" 
                  onClick={handleAddOption}
                  style={{ fontSize: '12px' }}
                >
<Plus size={14} style={{ verticalAlign: '-2px' }} /> Add Option
                </button>
              )}
              {pollOptions.length > 2 && (
                <button 
                  className="btn btn-ghost btn-sm" 
                  style={{ color: 'var(--red)' }}
                  onClick={handleRemoveLastOption}
                >
                  Remove Last
                </button>
              )}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
              {pollOptions.filter(o => o.trim()).length} of {pollOptions.length} options filled
              {pollOptions.filter(o => o.trim()).length < 2 && (
                <span style={{ color: 'var(--red)', marginLeft: '4px' }}>· Need at least 2</span>
              )}
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Expires In</label>
            <select 
              className="form-select" 
              ref={pollExpiresInRef}
              defaultValue={pollExpiresIn}
            >
              <option value="1 day">1 day</option>
              <option value="3 days">3 days</option>
              <option value="7 days">7 days</option>
              <option value="14 days">14 days</option>
              <option value="30 days">30 days</option>
            </select>
          </div>

          <div style={{ 
            marginTop: '16px',
            padding: '12px',
            background: 'var(--cream)',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '8px', fontWeight: '600' }}>
<BarChart3 size={12} style={{ verticalAlign: '-2px' }} /> Preview
            </div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)', marginBottom: '8px' }}>
              {pollQuestion || 'Your poll question will appear here'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {pollOptions.map((option, index) => (
                option.trim() && (
                  <div key={`preview-${index}`} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    padding: '4px 8px',
                    background: 'white',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    <span style={{ 
                      minWidth: '20px',
                      fontSize: '10px',
                      fontWeight: '700',
                      color: 'var(--text-light)'
                    }}>
                      {String.fromCharCode(65 + index)}.
                    </span>
                    <span>{option}</span>
                  </div>
                )
              ))}
            </div>
            {pollOptions.filter(o => o.trim()).length === 0 && (
              <div style={{ fontSize: '12px', color: 'var(--text-light)', textAlign: 'center' }}>
                Add at least 2 options to see preview
              </div>
            )}
            {pollOptions.filter(o => o.trim()).length > 0 && pollOptions.filter(o => o.trim()).length < 2 && (
              <div style={{ fontSize: '12px', color: 'var(--text-light)', textAlign: 'center' }}>
                Add 1 more option to complete the poll
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              // Save values from refs to state
              savePollValues();
              
              const question = pollQuestionRef.current?.value?.trim() || '';
              const options = pollOptionRefs.current.map(ref => ref?.value?.trim() || '');
              const validOptions = options.filter(o => o.trim());
              
              if (!question) {
                showToast('Please enter a question', 'error');
                return;
              }
              if (validOptions.length < 2) {
                showToast('Please add at least 2 options', 'error');
                return;
              }
              markStepDone(step.id);
              showToast(`✅ ${step.title} completed!`, 'success');
              if (step.path && onNavigate) {
                onNavigate(step.path);
              }
              closeModal();
            }}
          >
<BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Create & Complete
          </button>
        </div>
      </div>
    );
  };

  // Generic modal fallback
  const openGenericModal = (step) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><step.icon size={16} style={{ verticalAlign: '-3px' }} /> Step {step.n}: {step.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            {step.desc}
          </p>
          <div style={{ 
            padding: '20px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><step.icon size={48} strokeWidth={1.5} /></div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--navy)' }}>
              Ready to start {step.title}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginTop: '4px' }}>
              This will open the {step.title} section.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              markStepDone(step.id);
              showToast(`✅ ${step.title} completed!`, 'success');
              if (step.path && onNavigate) {
                onNavigate(step.path);
              }
              closeModal();
            }}
          >
<Rocket size={16} style={{ verticalAlign: '-3px' }} /> Start & Complete
          </button>
        </div>
      </div>
    );
  };

  // Complete step (Mark as Done)
  const handleCompleteStep = (step) => {
    if (step.done) {
      showToast(`✅ ${step.title} is already completed`, 'info');
      return;
    }

    let hasData = false;
    switch(step.component) {
      case 'ChurchProfile':
        hasData = churchProfile.name && churchProfile.pastor && churchProfile.phone;
        break;
      case 'SmsProvider':
        hasData = smsProvider.provider && smsProvider.apiKey;
        break;
      case 'ShortcodeSetup':
        hasData = shortcodeData.shortcode && shortcodeData.keyword;
        break;
      case 'BulkImport':
        hasData = true;
        break;
      case 'BirthdayAutomation':
        hasData = birthdayData.message;
        break;
      case 'Broadcast':
        hasData = broadcastData.message;
        break;
      case 'PollSetup':
        hasData = pollQuestion && pollOptions.filter(o => o.trim()).length >= 2;
        break;
      default:
        hasData = true;
    }

    if (!hasData) {
      showToast(`⚠️ Please complete the ${step.title} setup first`, 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><CheckCircle2 size={16} style={{ verticalAlign: '-3px' }} /> Complete Step {step.n}: {step.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Have you completed <strong>{step.title}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--green-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--green)' 
          }}>
<CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> Marking this as done will update your onboarding progress.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              markStepDone(step.id);
              showToast(`✅ ${step.title} completed!`, 'success');
              closeModal();
            }}
          >
<CheckCircle2 size={16} style={{ verticalAlign: '-3px' }} /> Mark as Done
          </button>
        </div>
      </div>
    );
  };

  // Skip step
  const handleSkipStep = (step) => {
    if (step.done) {
      showToast(`⚠️ ${step.title} is already completed`, 'info');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><SkipForward size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Skip Step {step.n}: {step.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to skip <strong>{step.title}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--amber-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--amber)' 
          }}>
<AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> You can always come back to complete this step later.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-ghost btn-sm" 
            onClick={() => {
              setSteps(steps.map(s => ({
                ...s,
                current: false
              })));
              const nextStep = steps.find(s => !s.done && s.id !== step.id);
              if (nextStep) {
                setSteps(steps.map(s => ({
                  ...s,
                  current: s.id === nextStep.id
                })));
              }
              showToast(`⏭️ Skipped ${step.title}`, 'info');
              closeModal();
            }}
          >
            Skip Step
          </button>
        </div>
      </div>
    );
  };

  // Reset onboarding
  const handleResetOnboarding = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><RefreshCw size={16} style={{ verticalAlign: '-3px' }} /> Reset Onboarding</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to reset all onboarding progress?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
<AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will mark all steps as pending and reset your progress.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            onClick={() => {
              setSteps(steps.map((s, index) => ({
                ...s,
                done: false,
                current: index === 0
              })));
              setChurchProfile({ name: '', pastor: '', phone: '', email: '', address: '', website: '' });
              setSmsProvider({ provider: '', apiKey: '', shortcode: '', senderId: '' });
              setShortcodeData({ shortcode: '', keyword: '', flowSteps: [] });
              setBirthdayData({ enabled: false, message: 'Happy Birthday {FirstName}! 🎂 May your day be filled with God\'s love and blessings! 🙏', sendTime: '08:00', timezone: 'GMT' });
              setBroadcastData({ message: '', recipients: 'All Members', scheduleDate: '' });
              setPollQuestion('');
              setPollOptions(['', '']);
              setPollExpiresIn('7 days');
              // Reset refs
              pollOptionRefs.current = [];
              showToast('🔄 Onboarding reset', 'info');
              closeModal();
            }}
          >
            Reset Onboarding
          </button>
        </div>
      </div>
    );
  };

  // Get the next pending step
  const getNextPendingStep = () => {
    return steps.find(s => !s.done);
  };

  // Move to next step
  const handleNextStep = () => {
    const nextStep = getNextPendingStep();
    if (nextStep) {
      handleStartStep(nextStep);
    } else {
      showToast('🎉 All steps completed!', 'success');
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          Complete these steps to fully activate your FaithConnect SMS platform.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={handleResetOnboarding}>
            <RefreshCw size={14} style={{ verticalAlign: '-2px' }} /> Reset Progress
          </button>
          {getNextPendingStep() && (
            <button className="btn btn-gold btn-sm" onClick={handleNextStep}>
              Next Step →
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div className="card-title">Setup Progress</div>
            <div style={{ fontSize: '13px', color: 'var(--text-light)', marginTop: '4px' }}>
              {completedCount} of {steps.length} steps completed
            </div>
          </div>
          <div style={{ fontSize: '36px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{pct}%</div>
        </div>
        <div className="progress-bar" style={{ height: '10px', marginBottom: '6px' }}>
          <div className="progress-fill" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, var(--navy), var(--gold))' }}></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-light)' }}>
          <span>{steps.length - completedCount} steps remaining</span>
          <span>{completedCount} completed</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {steps.map(s => (
          <div key={s.id} className="card" style={{ 
            borderLeft: `4px solid ${s.done ? 'var(--green)' : s.current ? 'var(--gold)' : 'var(--border)'}`,
            transition: 'all 0.3s ease',
            cursor: s.done ? 'default' : 'pointer'
          }}
          onClick={() => !s.done && handleStartStep(s)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ 
                width: '46px', 
                height: '46px', 
                borderRadius: '12px', 
                background: s.done ? 'var(--green-bg)' : s.current ? 'var(--gold-pale)' : 'var(--cream)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '22px', 
                flexShrink: 0 
              }}>
                {s.done ? <CheckCircle2 size={22} /> : <s.icon size={22} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: s.current ? '700' : '600', 
                  color: s.done ? 'var(--text-mid)' : s.current ? 'var(--navy)' : 'var(--text)' 
                }}>
                  Step {s.n}: {s.title} 
                  {s.current && <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--gold)', marginLeft: '8px' }}>← CURRENT</span>}
                  {s.done && <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--green)', marginLeft: '8px' }}><Check size={11} style={{ verticalAlign: '-1px' }} /> COMPLETED</span>}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '2px' }}>{s.desc}</div>
              </div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                {s.done ? (
                  <span className="badge badge-green" style={{ padding: '4px 12px' }}><Check size={12} style={{ verticalAlign: '-2px' }} /> Done</span>
                ) : s.current ? (
                  <>
                    <button className="btn btn-gold btn-sm" onClick={() => handleStartStep(s)}>
                      Start →
                    </button>
                    <button 
                      className="btn btn-ghost btn-sm btn-icon" 
                      onClick={() => handleCompleteStep(s)}
                      title="Mark as Done"
                      style={{ color: 'var(--green)' }}
                    >
                      <CheckCircle2 size={16} />
                    </button>
                    <button 
                      className="btn btn-ghost btn-sm btn-icon" 
                      onClick={() => handleSkipStep(s)}
                      title="Skip"
                      style={{ color: 'var(--amber)' }}
                    >
                      <SkipForward size={16} />
                    </button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleStartStep(s)}>
                      Start
                    </button>
                    <button 
                      className="btn btn-ghost btn-sm btn-icon" 
                      onClick={() => handleCompleteStep(s)}
                      title="Mark as Done"
                      style={{ color: 'var(--green)' }}
                    >
                      <CheckCircle2 size={16} />
                    </button>
                    <button 
                      className="btn btn-ghost btn-sm btn-icon" 
                      onClick={() => handleSkipStep(s)}
                      title="Skip"
                      style={{ color: 'var(--amber)' }}
                    >
                      <SkipForward size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ 
        marginTop: '20px', 
        padding: '20px', 
        background: 'linear-gradient(135deg, var(--navy), var(--navy-light))', 
        borderRadius: '16px', 
        color: 'white', 
        textAlign: 'center' 
      }}>
        <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><PartyPopper size={22} /></div>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '700', marginBottom: '6px' }}>
          Need help getting started?
        </div>
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,.7)', marginBottom: '16px' }}>
          Our support team is available Mon–Fri, 9 AM – 5 PM WAT.
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button 
            className="btn" 
            style={{ background: 'var(--gold)', color: 'var(--navy)', fontWeight: '600' }} 
            onClick={() => {
              openModal(
                <div>
                  <div className="modal-header">
                    <div className="modal-title"><MessageCircle size={16} style={{ verticalAlign: '-3px' }} /> WhatsApp Support</div>
                    <button className="modal-close" onClick={closeModal}>×</button>
                  </div>
                  <div className="modal-body">
                    <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
                      Connect with our support team via WhatsApp for immediate assistance.
                    </p>
                    <div style={{ 
                      padding: '16px', 
                      background: 'var(--cream)', 
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><Smartphone size={48} strokeWidth={1.5} /></div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--navy)' }}>
                        +233 24 123 4567
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
                        Available Mon–Fri, 9 AM – 5 PM WAT
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
                    <button 
                      className="btn btn-gold btn-sm" 
                      onClick={() => {
                        window.open('https://wa.me/233241234567', '_blank');
                        closeModal();
                      }}
                    >
                      Open WhatsApp
                    </button>
                  </div>
                </div>
              );
            }}
          >
<MessageCircle size={16} style={{ verticalAlign: '-3px' }} /> WhatsApp Support
          </button>
          <button 
            className="btn" 
            style={{ background: 'rgba(255,255,255,.1)', color: 'white', border: '1px solid rgba(255,255,255,.2)' }} 
            onClick={() => {
              openModal(
                <div>
                  <div className="modal-header">
                    <div className="modal-title"><BookOpen size={16} style={{ verticalAlign: '-3px' }} /> Documentation</div>
                    <button className="modal-close" onClick={closeModal}>×</button>
                  </div>
                  <div className="modal-body">
                    <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
                      Access our comprehensive documentation to get the most out of FaithConnect.
                    </p>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {[
                        { title: 'Getting Started Guide', icon: Rocket, desc: 'Quick setup guide for new users' },
                        { title: 'SMS Broadcasting', icon: Send, desc: 'Learn how to send SMS campaigns' },
                        { title: 'Member Management', icon: Users, desc: 'Import and manage your members' },
                        { title: 'API Documentation', icon: Plug, desc: 'Integrate with our API' }
                      ].map(doc => (
                        <div key={doc.title} style={{ 
                          padding: '12px', 
                          background: 'var(--cream)', 
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--gold-pale)';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'var(--cream)';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                        onClick={() => {
                          showToast(`📖 Opening ${doc.title}...`, 'info');
                          closeModal();
                        }}
                        >
                          <span style={{ display: 'inline-flex' }}><doc.icon size={22} /></span>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
                              {doc.title}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                              {doc.desc}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
                    <button 
                      className="btn btn-gold btn-sm" 
                      onClick={() => {
                        showToast('📖 Opening documentation...', 'info');
                        closeModal();
                      }}
                    >
                      View All Docs
                    </button>
                  </div>
                </div>
              );
            }}
          >
<BookOpen size={16} style={{ verticalAlign: '-3px' }} /> View Documentation
          </button>
        </div>
      </div>

      <style>{`
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-gold {
          background: var(--gold-pale);
          color: var(--gold);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .btn-danger {
          background: var(--red);
          color: white;
          border: none;
        }
        .btn-danger:hover {
          background: var(--red-dark);
        }
        .poll-options-container {
          margin-bottom: 4px;
        }
        @media (max-width: 768px) {
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
        }
      `}</style>
    </>
  );
};

export default Onboarding;